<template>
  <DashboardPanel
    title="My Pathway"
    :href="outcomeTileContent.buttonUrl"
    data-test="md-outcome-tile"
  >
    <div v-if="isDataLoaded" class="outcome-panel-content">
      <TileKebabMenu
        data-test="kebab-menu"
        v-if="!!currentPathway?.cmsPersonalPathwayId && !currentPathway?.isCompleted"
        ref="outcomeTileKebab"
        @openKebab="onKebabOpen"
      >
        <li>
          <a
            href="javascript:void(0)"
            @click.stop.prevent="handleChangeOutcome"
            data-test="change-outcome"
            >Change pathway</a
          >
        </li>
      </TileKebabMenu>
      <div
        v-if="!isUserOnAPathway"
        data-test="md-outcome-tile-default"
        class="default-outcome-content center-flex"
      >
        <transition name="fade" mode="out-in">
          <div v-show="isDefaultImageLoaded" class="outcome-panel-content team-outcome">
            <img
              alt="choose your pathway image"
              src="../../assets/jyranians-mountain.png"
              class="outcome-tile-default-img"
              @load="handleDefaultImageLoad"
              data-test="md-outcome-tile-default-img"
            />
            <h3 data-test="md-outcome-tile-default-title">Choose your pathway</h3>
            <button
              style="margin-bottom: 10px"
              class="btn btn-primary btn-large"
              data-test="md-outcome-tile-button"
            >
              Go
            </button>
          </div>
        </transition>
      </div>
      <transition name="fade" mode="out-in">
        <div
          v-if="isUserOnAPathway"
          class="selected-outcome-content"
          data-test="md-outcome-tile-content"
        >
          <div class="outcome-tile-image-container">
            <img
              :src="outcomeTileContent.outcomeImgSrc"
              :alt="`${cms.asText(outcomeTileContent.outcomeTitle)} image`"
              class="outcome-tile-outcome-image"
              data-test="md-outcome-tile-outcome-img"
            />
          </div>
          <div
            v-html="cms.asHtml(outcomeTileContent.outcomeTitle)"
            class="outcome-tile-outcome-title"
            data-test="md-outcome-tile-outcome-title"
          ></div>
          <div class="outcome-tile-image-container pathway-image">
            <img
              :src="outcomeTileContent.pathwayImgSrc"
              :alt="`${cms.asText(outcomeTileContent.pathwayTitle)} image`"
              class="outcome-tile-pathway-image"
              data-test="md-outcome-tile-pathway-img"
            />
          </div>
          <h4
            v-html="cms.asText(outcomeTileContent.pathwayTitle)"
            class="outcome-tile-pathway-title"
            data-test="md-outcome-tile-pathway-title"
          ></h4>
          <Stepper
            v-if="isUserOnAPathway && !currentPathway.dateCompleted"
            :completed-step="completedStep"
            :steps-count="pathwayStepCount"
            :show-tooltip="true"
            data-test="md-outcome-tile-progress"
          />
          <button class="btn btn-primary btn-large" data-test="md-outcome-tile-button">
            {{ outcomeTileContent.buttonText }}
          </button>
        </div>
      </transition>
    </div>
    <LoadingPage v-else />
  </DashboardPanel>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import Stepper from "../team-outcomes/progress-stepper.vue";
import DashboardPanel from "../dashboard-panel.vue";
import {
  CmsPersonalGoal,
  CmsPersonalPathway,
  CmsRichText,
} from "../../services/cms/cms-type-definitions";
import {
  PersonalPathwayUserProgressApiModel,
  PersonalPathwayCompletedStepApiModel,
  PersonalPathwayProgressApiModel,
} from "../../api-models";
import cms from "../../services/cms/cms";
import { useAxios } from "../../composables/api";
import LoadingPage from "../../views/loading-page.vue";
import TileKebabMenu from "../tile-kebab-menu.vue";

export type OutcomeTileButtonText = "Go" | "Continue" | "Complete pathway";

export interface PersonalOutcomeTile {
  outcomeImgSrc?: string;
  outcomeTitle?: string | CmsRichText;
  pathwayImgSrc?: string;
  pathwayTitle?: string | CmsRichText;
  buttonText: OutcomeTileButtonText;
  buttonUrl: string;
}

const outcomeTileKebab = ref();

const emit = defineEmits(["openKebab", "update:isChangeOutcomeModalDisplayed"]);

const isDataLoaded = ref(false);

const isDefaultImageLoaded = ref(false);

const pathwayDoc = ref<CmsPersonalPathway | null>(null);

const currentOutcome = ref<CmsPersonalGoal | null>(null);

const pathwayProgress = ref<PersonalPathwayCompletedStepApiModel | null>(null);

const currentPathway = ref<PersonalPathwayUserProgressApiModel | null>(null);

const isUserOnAPathway = ref(false);

const pathwayStepCount = computed<number>(() => {
  return pathwayDoc.value?.data.pathway_steps.length ?? 0;
});

const completedStep = computed<number>(() => {
  return pathwayProgress.value?.stepIndex ?? -1;
});

const handleChangeOutcome = (): void => {
  emit("update:isChangeOutcomeModalDisplayed", true);
};

const handleDefaultImageLoad = () => {
  isDefaultImageLoaded.value = true;
};

const onKebabOpen = () => {
  emit("openKebab");
};

const hasUserCompletedLastStep = (): boolean => {
  if (pathwayProgress.value?.stepIndex && pathwayDoc.value) {
    const pathwaySteps = pathwayDoc.value.data.pathway_steps;
    const finalStep = pathwaySteps[pathwaySteps.length - 1];
    return finalStep.pathway_step.id === pathwayProgress.value.cmsStepId && isUserOnAPathway.value;
  }
  return false;
};

const outcomeTileContent = computed<PersonalOutcomeTile>(() => {
  const isReadyToComplete = hasUserCompletedLastStep();

  let buttonText = "Go";
  if (isUserOnAPathway.value) {
    buttonText = isReadyToComplete ? "Complete pathway" : "Continue";
  }

  let buttonUrl = "/my/outcomes";
  if (isUserOnAPathway.value && currentPathway.value) {
    buttonUrl = isReadyToComplete
      ? "/my/complete"
      : `/my/pathway/${currentPathway.value.cmsPersonalPathwayUid}`;
  }

  return {
    outcomeImgSrc: currentOutcome.value?.data.image.url,
    outcomeTitle: currentOutcome.value?.data.full_title,
    pathwayImgSrc: pathwayDoc.value?.data.title_image.url,
    pathwayTitle: pathwayDoc.value?.data.title,
    buttonText,
    buttonUrl,
  } as PersonalOutcomeTile;
});

const getCurrentOutcomeAndPathwayData = async (
  pathwayData: PersonalPathwayProgressApiModel,
): Promise<void> => {
  currentPathway.value = pathwayData.currentPathway;
  pathwayProgress.value = pathwayData.pathwayProgress;
  pathwayDoc.value = await cms.getById(
    "cms-personal-pathway",
    currentPathway.value.cmsPersonalPathwayId,
  );
  currentOutcome.value = await cms.getById(
    "cms-personal-goal",
    currentPathway.value.cmsPersonalOutcomeId,
  );
};

const { promise } = useAxios<PersonalPathwayProgressApiModel>("/api/personalpathway/current");
promise.then((pathwayData) => {
  if (pathwayData.currentPathway) {
    getCurrentOutcomeAndPathwayData(pathwayData).then(() => {
      isUserOnAPathway.value = !pathwayData.currentPathway.isCompleted;
      isDataLoaded.value = true;
    });
  } else {
    isDataLoaded.value = true;
  }
});

defineExpose({
  outcomeTileKebab,
  isUserOnAPathway,
});
</script>

<style scoped lang="less">
@import "../../styles/dashboards/dashboards.less";
</style>

<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.outcome-panel-content h3 strong {
  color: @dark-background-color;
}
</style>
